import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  Stack,
  Alert,
  Table,
  ActionIcon,
  Card,
  Text,
  Loader,
  Center,
  Box,
} from '@mantine/core';
import { IconAlertCircle, IconEye, IconPencil, IconFileReport } from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { plantsApi } from '../../services/plantsApi';
import { reportsApi } from '../../services/reportsApi';
import { ReportFilters } from '../../components/reports/ReportFilters';
import { ReportStatusBadge } from '../../components/reports/ReportStatusBadge';
import { PageHeader } from '../../components/ui/PageHeader';
import type { Plant } from '../../types/entities';
import { formatPeriod } from '../../utils/reportPeriod';
import { reportingPeriodRange } from '../../utils/reportYearRange';
import { deriveIntervals, periodSelectorInterval, prunePeriod } from '../../utils/reportIntervals';
import { visibleReportTypes } from '../../utils/reportAccess';
import {
  parseUrlFilters,
  filtersToSearchParams,
  resolveInitialSearchParams,
  reportsFilterStorageKey,
} from './reportsFilterUrl';
import { reportPermissions } from './reportPermissions';
import {
  ReportStatus,
  ReportTypeIdentifier,
  ReportTypeIdentifierLabels,
  type ReportFilters as ReportFiltersType,
  type ReportInterval,
  type FormularAB,
  type FormularC,
  type FormularD,
  type EnvironmentalData,
  type AccidentData,
} from '../../types/reports';

interface UnifiedReport {
  id: number;
  type: ReportTypeIdentifier;
  typeName: string;
  plantName: string;
  companyName: string;
  reportingPeriod: string;
  interval: ReportInterval;
  status: ReportStatus;
  createdAt: string;
}

function mapToUnifiedReport(
  report: FormularAB | FormularC | FormularD | EnvironmentalData | AccidentData,
  type: ReportTypeIdentifier
): UnifiedReport {
  return {
    id: report.id,
    type,
    typeName: ReportTypeIdentifierLabels[type],
    plantName: report.plant?.name ?? 'Unbekannt',
    companyName: report.plant?.company?.name ?? '',
    reportingPeriod: report.reportingPeriod,
    interval: report.configuration.interval,
    status: report.status,
    createdAt: report.createdAt,
  };
}

// D-19: erster localStorage-Schluessel des Projekts, je Benutzer, weil mehrere Personen denselben
// Browser nutzen koennen. Ein gespeicherter Stand traegt immer `year`, deshalb bleibt die
// Jahres-Vorbelegung (Phase 25 D-05/D-06) unberuehrt. Fehlender Speicher darf die Seite nicht
// brechen.
function readStoredFilters(userId: string | undefined): string | null {
  if (!userId) return null;
  try {
    return localStorage.getItem(reportsFilterStorageKey(userId));
  } catch {
    return null;
  }
}

function writeStoredFilters(userId: string | undefined, value: string): void {
  if (!userId) return;
  try {
    localStorage.setItem(reportsFilterStorageKey(userId), value);
  } catch {
    // Speicher nicht verfuegbar -- der Filter funktioniert trotzdem ueber die URL.
  }
}

export function ReportsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const [reports, setReports] = useState<UnifiedReport[]>([]);
  const [plants, setPlants] = useState<Plant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // D-19: Ohne URL-Parameter (Aufruf ueber das Menue) gilt der zuletzt gemerkte Stand dieses
  // Benutzers; ein Link mit eigenen Parametern gewinnt. `user` ist hier bereits geladen, weil
  // ProtectedRoute in App.tsx waehrend `isLoading` nichts rendert -- der Restore geht also nicht
  // verloren.
  const [filters, setFilters] = useState<ReportFiltersType>(() =>
    parseUrlFilters(resolveInitialSearchParams(searchParams, readStoredFilters(user?.id)))
  );
  const [initialLoad, setInitialLoad] = useState(true);

  // D-14/D-15/D-16: die einzige Quelle der angezeigten Intervalle sind die geladenen Zeilen
  // (`deriveIntervals`), NICHT `filters.reportType` und keine Rollenableitung. Bleibt `reports`
  // beim Nachladen kurz leer, haelt `deriveIntervals` den vorherigen Stand -- der Selektor
  // verschwindet nicht genau dann, wenn man ihn zum Zuruecksetzen braucht.
  const [lastIntervals, setLastIntervals] = useState<ReportInterval[]>([]);

  // Bewusst nur [reports] als Abhaengigkeit: lastIntervals wird gelesen, nicht als Trigger
  // gebraucht; der join(',')-Vergleich oben verhindert die sonst aus einem setState-im-Effekt
  // entstehende Renderschleife.
  useEffect(() => {
    const next = deriveIntervals(reports, lastIntervals);
    if (next.join(',') !== lastIntervals.join(',')) {
      setLastIntervals(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports]);

  const selectorInterval = periodSelectorInterval(lastIntervals, filters.year);

  // D-17: Strings/`undefined` statt des Ergebnisobjekts als useCallback-Abhaengigkeit von
  // fetchReports (siehe dort) -- ein frisches Objekt bekaeme bei jedem Render eine neue
  // Identitaet und wuerde bei jedem Intervallwechsel eine ueberfluessige Abfrage ausloesen,
  // selbst wenn sich die beiden Grenzen inhaltlich gar nicht aendern.
  const { reportingPeriodAfter, reportingPeriodBefore } = reportingPeriodRange(
    filters.year,
    filters.period,
    selectorInterval
  );

  // D-10/D-12: einzige Quelle der geladenen Meldungstypen -- dieselbe Funktion, die auch das
  // Dropdown in ReportFilters.tsx speist. `useMemo`, damit die Liste eine stabile Identitaet
  // hat und als Abhaengigkeit von `fetchReports` keine Endlosschleife ausloest.
  const availableTypes = useMemo(() => visibleReportTypes(user), [user]);

  const fetchPlants = useCallback(async () => {
    try {
      const data = await plantsApi.getAll();
      setPlants(data);
    } catch (err) {
      console.error('Failed to fetch plants:', err);
    }
  }, []);

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // D-17: die Meldungenseite schickt ausschliesslich den Bereich, keinen Exact-Parameter.
      // `year`/`period` reisen im Spread mit -- `buildQueryParams` wertet sie nicht aus.
      const { formularAB, formularC, formularD, environmental, accident } = await reportsApi.getAllReports({
        ...filters,
        reportingPeriodAfter,
        reportingPeriodBefore,
      });

      const unified: UnifiedReport[] = [];

      // D-12: Dropdown (ReportFilters.tsx) und Ladelogik lasen bisher aus zwei getrennten,
      // bereits auseinandergelaufenen Rollenpruefungen -- die hiesige kannte die freigeschalteten
      // Kategorien ueberhaupt nicht. Ein Treuhaender ohne `market`-Kategorie sah deshalb den
      // Hinweis "keine Meldungsart freigeschaltet" und bekam bei `?type=formular-ab` trotzdem
      // Formular-AB-Zeilen geladen. Jetzt entscheidet eine einzige Funktion (`availableTypes`)
      // ueber beide Achsen -- Rolle UND Kategorie. Bleibt `availableTypes` leer (D-11), laeuft
      // die Schleife nicht und die Tabelle zeigt den bestehenden Leerzustand.
      // Das ist weiterhin Frontend-Komfort, keine Durchsetzung: die harte Grenze bleibt
      // `ReportFilterExtension` im Backend, die einem nicht freigeschalteten Nutzer ohnehin
      // nichts liefert.
      const rowsByType: Record<
        ReportTypeIdentifier,
        ReadonlyArray<FormularAB | FormularC | FormularD | EnvironmentalData | AccidentData>
      > = {
        [ReportTypeIdentifier.FORMULAR_AB]: formularAB,
        [ReportTypeIdentifier.FORMULAR_C]: formularC,
        [ReportTypeIdentifier.FORMULAR_D]: formularD,
        [ReportTypeIdentifier.ENVIRONMENTAL]: environmental,
        [ReportTypeIdentifier.ACCIDENT]: accident,
      };

      for (const type of availableTypes) {
        if (filters.reportType && filters.reportType !== type) {
          continue;
        }
        unified.push(...rowsByType[type].map((r) => mapToUnifiedReport(r, type)));
      }

      unified.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      setReports(unified);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      setError('Fehler beim Laden der Meldungen');
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [filters, reportingPeriodAfter, reportingPeriodBefore, availableTypes]);

  // D-06: kein zweiter Effekt fuer die Vorbelegung noetig -- `initialLoad` wird im `finally` von
  // `fetchReports` auf `false` gesetzt, dieser Effekt laeuft dadurch direkt nach dem ersten
  // Ladevorgang und schreibt den vorbelegten Zustand (das laufende Jahr) in die URL. Genau so ist
  // D-06 erfuellt, ohne zwei konkurrierende Schreiber auf `searchParams`.
  // Der Ersetzen-statt-Anhaengen-Schalter unten ist noetig, damit das automatische Schreiben der
  // Vorbelegung KEINEN zusaetzlichen Verlaufseintrag erzeugt -- sonst fuehrte der Zurueck-Schalter
  // auf die parameterlose URL, die sich sofort wieder selbst umschreibt (Schleife ohne Ausweg).
  useEffect(() => {
    if (!initialLoad) {
      const params = filtersToSearchParams(filters);
      setSearchParams(params, { replace: true });
      writeStoredFilters(user?.id, params.toString());
    }
  }, [filters, initialLoad, setSearchParams, user?.id]);

  // D-08: faellt der Selektor durch einen Intervallwechsel weg, nimmt die gewaehlte Periode
  // sowohl den Filterzustand als auch (ueber den Sync-Effekt oben) die URL mit. Die Bedingung
  // verhindert einen neuen `filters`-Zustand bei jedem Durchlauf und damit eine Endlosschleife.
  useEffect(() => {
    const pruned = prunePeriod(filters.period, selectorInterval);
    if (pruned !== filters.period) {
      setFilters((prev) => ({ ...prev, period: pruned }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bewusst nur [selectorInterval]
  }, [selectorInterval]);

  useEffect(() => {
    fetchPlants();
  }, [fetchPlants]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleEdit = (report: UnifiedReport) => {
    navigate(`/reports/${report.type}/${report.id}`);
  };

  const handleView = (report: UnifiedReport) => {
    navigate(`/reports/${report.type}/${report.id}`);
  };

  const filteredReports = useMemo(() => {
    return reports;
  }, [reports]);

  // D-17: kein Genehmigen in der Liste -- genehmigt wird nur in der geoeffneten Meldung.
  // D-08/D-20: "Bearbeiten" genau dann, wenn die Rechtefunktion es erlaubt, sonst "Ansehen".
  const renderActions = (report: UnifiedReport) => {
    if (reportPermissions(user, report.type, report.status).canEdit) {
      return (
        <ActionIcon
          variant="outline"
          color="vdzGray"
          onClick={() => handleEdit(report)}
          title="Bearbeiten"
        >
          <IconPencil size={16} />
        </ActionIcon>
      );
    }

    return (
      <ActionIcon
        variant="outline"
        color="vdzGray"
        onClick={() => handleView(report)}
        title="Ansehen"
      >
        <IconEye size={16} />
      </ActionIcon>
    );
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title="Meldungen verwalten"
        breadcrumb={{ label: 'Meldungen' }}
      />

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Fehler"
          color="red"
          onClose={() => setError(null)}
          withCloseButton
        >
          {error}
        </Alert>
      )}

      {/* Filters panel */}
      <Card padding="md" radius="md" withBorder>
        <ReportFilters
          filters={filters}
          onFiltersChange={setFilters}
          plants={plants}
          loading={loading}
          periodInterval={selectorInterval}
        />
      </Card>

      {/* Data table */}
      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : filteredReports.length === 0 ? (
        <Card padding="xl" radius="md" withBorder>
          <Center>
            <Stack align="center" gap="sm">
              <IconFileReport size={36} color="var(--mantine-color-vdzGray-5)" />
              <Text fz={14} fw={500} c="vdzGray.7">Keine Meldungen vorhanden</Text>
              <Text fz={12} c="vdzGray.6">Es wurden keine Meldungen mit den aktuellen Filtern gefunden.</Text>
            </Stack>
          </Center>
        </Card>
      ) : (
        <Card>
          <Box style={{ overflowX: 'auto' }}>
            <Table highlightOnHover striped="even">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Meldung</Table.Th>
                  <Table.Th>Werk</Table.Th>
                  <Table.Th>Zeitraum</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th style={{ width: 200 }}>Aktionen</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredReports.map((report) => (
                  <Table.Tr
                    key={`${report.type}-${report.id}`}
                  >
                    <Table.Td>{report.typeName}</Table.Td>
                    <Table.Td>
                      {report.plantName}
                      {report.companyName ? ` (${report.companyName})` : ''}
                    </Table.Td>
                    <Table.Td>{formatPeriod(report.reportingPeriod, report.interval)}</Table.Td>
                    <Table.Td>
                      <ReportStatusBadge status={report.status} />
                    </Table.Td>
                    <Table.Td>{renderActions(report)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>
        </Card>
      )}
    </Stack>
  );
}
